"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Logo from "../../../components/ui/logo";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/forgot-password/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workEmail: email, phoneNumber: phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur de vérification");
      setResetToken(data.resetToken);
      setStep(2);
      setError("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resetToken, newPassword: password }),
      });
      if (!res.ok) throw new Error("Erreur lors de la mise à jour");
      router.push("/signin");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

 return (
    // Use flex-col and h-screen to force the entire layout into the viewport
    <div className="h-screen flex flex-col bg-[#e0f2fe] relative overflow-hidden font-sans">
      
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-blue-200/50 blur-[100px]"></div>
        <div className="absolute bottom-[10%] left-[-5%] w-[300px] h-[300px] rounded-full bg-blue-100/60 blur-[80px]"></div>
      </div>

      {/* Reduced padding (py-4) to save vertical space */}
      <main className="flex-grow flex flex-col items-center justify-center px-4 z-10 py-4">
        
        {/* Title made smaller: text-2xl/3xl instead of 4xl/5xl */}
        <h1 className="text-[#3b608d] text-2xl md:text-3xl font-semibold mb-6 tracking-tight">
          {step === 1 ? "Récupérer votre compte" : "Nouveau mot de passe"}
        </h1>

        {/* Compact Card: Reduced padding p-6 instead of p-10 */}
        <div className="w-full max-w-[380px] bg-white/40 backdrop-blur-md border border-white/20 rounded-[20px] p-6 shadow-sm">
          <form onSubmit={step === 1 ? handleVerify : handleReset} className="space-y-4">
            {step === 1 ? (
              <>
                <div>
                  <label className="block text-[#4a6d96] text-xs mb-1">Email <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white/80 text-sm text-black focus:ring-2 focus:ring-blue-300"
                    placeholder="amine.mhedhbi@cweave.fr"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[#4a6d96] text-xs mb-1">Téléphone <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white/80 text-sm text-black focus:ring-2 focus:ring-blue-300"
                    placeholder="Votre numéro"
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-[#4a6d96] text-xs mb-1">Nouveau mot de passe <span className="text-red-400">*</span></label>
                <input
                  type="password"
                  className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white/80 text-sm text-black focus:ring-2 focus:ring-blue-300"
                  placeholder="••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#5b96a2] hover:bg-[#4d828d] text-white rounded-lg font-medium text-sm transition-colors"
            >
              {loading ? "Chargement..." : (step === 1 ? "Vérifier" : "Réinitialiser")}
            </button>
          </form>
        </div>

        {/* Bottom Links: Margins reduced */}
        <div className="mt-4 text-center space-y-2">
          <p className="text-[#4a6d96] text-xs">
            Vous n'avez pas de compte ?{" "}
            <Link href="/signup" className="text-[#4fd1c5] hover:underline">S'inscrire</Link>
          </p>
          <p className="text-[#4a6d96] text-xs">
             <Link href="/signin" className="text-[#4fd1c5] hover:underline">Retour à la connexion</Link>
          </p>
        </div>
      </main>

      {/* Footer: Made significantly more compact */}
      <section className="flex items-center justify-between border-t border-white/20 py-3 px-6 shrink-0">
        <div className="scale-75 origin-left">
          <Logo />
        </div>
        <p className="text-xs text-indigo-400/65">© 2025 Comweave</p>
        <ul className="inline-flex gap-2">
          <li>
            <a href="https://www.linkedin.com/company/comweave" className="text-teal-500">
              <svg className="h-5 w-5 fill-current" viewBox="0 0 32 32">
                <path d="M29 0H3C1.35 0 0 1.35 0 3v26c0 1.65 1.35 3 3 3h26c1.65 0 3-1.35 3-3V3c0-1.65-1.35-3-3-3ZM9.339 27H5.667V12.667h3.672V27ZM7.503 10.5c-1.173 0-2.125-.953-2.125-2.125 0-1.173.952-2.125 2.125-2.125 1.172 0 2.125.952 2.125 2.125 0 1.172-.953 2.125-2.125 2.125ZM27 27h-3.671V19.333c0-1.835-.037-4.2-2.557-4.2-2.56 0-2.953 2-2.953 4.066V27h-3.673V12.667h3.526v2.057h.05c.492-.933 1.697-1.917 3.495-1.917 3.737 0 4.423 2.463 4.423 5.663V27Z" />
              </svg>
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}